import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useUpload } from '../../../contexts/UploadContext';
import { useTranslation } from 'react-i18next';
import { ChevronLeft, ChevronRight, Layout, Map, DollarSign, Image as ImageIcon, Star, Loader2, CheckCircle2 } from 'lucide-react';
import { Button } from '../../../components/ui/Button';
import { CategoryService } from '../../../handling_data/services/CategoryService';
import { OwnerService, type Owner } from '../../../handling_data/services/OwnerService';
import { PropertyRepository } from '../../../handling_data/repositories/PropertyRepository';
import { PropertyService } from '../../../handling_data/services/PropertyService';
import { CreatePropertyDto } from '../../../handling_data/dtos/PropertyDto';
import { Category } from '../../../handling_data/models/Category';

// Modular Step Components
import BasicInfoStep, { StepRef } from './BasicInfoStep';
import LocationStep from './LocationStep';
import PricingStep from './PricingStep';
import FeaturesStep from './FeaturesStep';
import MediaStep, { MediaStepRef } from './MediaStep';
import ConfirmationStep from './ConfirmationStep';

export const PropertyForm: React.FC = () => {
    const { t, i18n } = useTranslation();
    const navigate = useNavigate();
    const { uploadFiles } = useUpload();
    const { id } = useParams();
    const [searchParams] = useSearchParams();

    // Determine initial step from URL or default to 0
    const allSteps = [
        { id: 'basic', label: t('property_form.steps.basic'), icon: Layout },
        { id: 'location', label: t('property_form.steps.location'), icon: Map },
        { id: 'pricing', label: t('property_form.steps.pricing'), icon: DollarSign },
        { id: 'features', label: t('property_form.features.title'), icon: Star },
        { id: 'media', label: t('property_form.steps.media'), icon: ImageIcon },
        { id: 'confirm', label: t('property_form.steps.confirm') || 'التأكيد', icon: CheckCircle2 },
    ];

    // Filter out media step if in edit mode
    const steps = id ? allSteps.filter(s => s.id !== 'media') : allSteps;

    // Determine initial step from URL or default to 0
    const getInitialStep = () => {
        const stepParam = searchParams.get('step');
        if (!stepParam) return 0;
        const index = steps.findIndex(s => s.id === stepParam);
        return index !== -1 ? index : 0;
    };

    const [currentStep, setCurrentStep] = useState(getInitialStep());
    const [categories, setCategories] = useState<Category[]>([]);
    const [owners, setOwners] = useState<Owner[]>([]);
    const [createdId] = useState<string | undefined>(id);

    // Step Refs for Buffered State
    const basicRef = useRef<StepRef>(null);
    const locationRef = useRef<StepRef>(null);
    const pricingRef = useRef<StepRef>(null);
    const featuresRef = useRef<StepRef>(null);
    const mediaRef = useRef<MediaStepRef>(null);

    // Global state for persistence
    const [mainImageFile, setMainImageFile] = useState<File | null>(null);
    const [galleryFiles, setGalleryFiles] = useState<File[]>([]);
    const [existingImages, setExistingImages] = useState<{ id: number, url: string, is_main: boolean }[]>([]);
    const [deletedMediaIds, setDeletedMediaIds] = useState<number[]>([]);

    const [formData, setFormData] = useState<CreatePropertyDto>({
        ar: { title: '', description: '', address: '' },
        en: { title: '', description: '', address: '' },
        tr: { title: '', description: '', address: '' },
        category_id: 0,
        user_id: 0,
        contact_info: '',
        rooms: '',
        bathrooms: '',
        area: '',
        is_active: true,
        lat: 41.0082,
        lng: 28.9784,
        price_daily: 0,
        price_monthly: 0,
        price_yearly: 0,
        additional_features: []
    });

    const [fetchingData, setFetchingData] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        const fetchData = async () => {
            setFetchingData(true);
            try {
                const [cats, ownrs] = await Promise.all([
                    CategoryService.getCategories(),
                    OwnerService.getOwners()
                ]);
                setCategories(cats.data);
                const ownersRaw = ownrs.data as any;
                const ownersList: any[] = Array.isArray(ownersRaw)
                    ? ownersRaw
                    : (ownersRaw?.data && Array.isArray(ownersRaw.data) ? ownersRaw.data : []);

                setOwners(ownersList);

                if (id) {
                    const prop = await PropertyService.getProperty(Number(id));
                    const p = prop.data;

                    if (p.media) {
                        setExistingImages(p.media.map((m: any) => ({
                            id: m.id,
                            url: m.url,
                            is_main: m.category === 'property_image'
                        })));
                    }

                    // Robust resolution of User ID from various possible backend structures
                    let resolvedUserId = 0;
                    if (p.owner_id) resolvedUserId = Number(p.owner_id);
                    else if (p.user_id) resolvedUserId = Number(p.user_id);
                    else if (p.owner?.id) resolvedUserId = Number(p.owner.id);
                    else if (p.user?.id) resolvedUserId = Number(p.user.id);

                    // Fallback Matching by Phone if ID logic fails
                    if (!resolvedUserId && (p.owner?.phone || p.contact_info) && ownersList.length > 0) {
                        const targetPhone = p.owner?.phone || p.contact_info;
                        const matched = ownersList.find(o => o.phone === targetPhone || o.profile?.contact_phone === targetPhone);
                        if (matched) resolvedUserId = matched.id;
                    }

                    // Mapping logic to extract values from the unified additional_features array or fallbacks
                    const findFeatureValue = (label: string) => {
                        const featuresArray = Array.isArray((p as any).additional_features)
                            ? (p as any).additional_features
                            : (Array.isArray((p as any).features) ? (p as any).features : []);
                        const feature = featuresArray.find((f: any) => f.label === label);
                        return feature ? feature.value?.toString() : null;
                    };

                    setFormData({
                        ar: {
                            title: prop.translations?.title?.ar || p.title || '',
                            description: prop.translations?.description?.ar || p.description || '',
                            address: p.location?.address || ''
                        },
                        en: {
                            title: prop.translations?.title?.en || prop.translations?.title?.ar || p.title || '',
                            description: prop.translations?.description?.en || prop.translations?.description?.ar || p.description || '',
                            address: p.location?.address || ''
                        },
                        tr: {
                            title: prop.translations?.title?.tr || prop.translations?.title?.ar || p.title || '',
                            description: prop.translations?.description?.tr || prop.translations?.description?.ar || p.description || '',
                            address: p.location?.address || ''
                        },
                        category_id: Number(p.category?.id || p.category_id || 0),
                        user_id: resolvedUserId,
                        contact_info: p.owner?.phone || p.contact_info || '',
                        rooms: findFeatureValue('rooms') || p.rooms?.toString() || (p as any).features?.rooms?.toString() || '',
                        bathrooms: findFeatureValue('bathrooms') || (p as any).bathrooms?.toString() || (p as any).features?.bathrooms?.toString() || '',
                        area: findFeatureValue('area') || (p as any).area?.toString() || (p as any).features?.area?.toString() || '',
                        is_active: p.is_active,
                        lat: p.location?.lat || p.lat || 0,
                        lng: p.location?.lng || p.lng || 0,
                        price_daily: Number(p.pricing?.daily) || p.price_daily,
                        price_monthly: Number(p.pricing?.monthly) || p.price_monthly,
                        price_yearly: Number(p.pricing?.yearly) || p.price_yearly,
                        additional_features: (
                            Array.isArray((p as any).additional_features)
                                ? (p as any).additional_features
                                : (Array.isArray((p as any).features) ? (p as any).features : (p.features?.additional || []))
                        )
                            .filter((f: any) => f && !['rooms', 'bathrooms', 'area'].includes(f.label))
                            .map((f: any) => typeof f === 'string' ? f : f.label)
                    });
                }
            } catch (err) {
                console.error('Failed to fetch initial data:', err);
                setError('فشل في تحميل البيانات الأولية.');
            } finally {
                setFetchingData(false);
            }
        };
        fetchData();
    }, [id]);

    useEffect(() => {
        const step = searchParams.get('step');
        if (step) {
            const index = steps.findIndex(s => s.id === step);
            if (index !== -1) setCurrentStep(index);
        }
    }, [searchParams, steps]);

    const validateStep = (stepIdx: number, stepData: any) => {
        setError(null);
        const stepId = steps[stepIdx].id;

        if (stepId === 'basic') {
            if (!stepData.ar.title || !stepData.category_id || !stepData.user_id) {
                setError('يرجى إكمال البيانات الأساسية (العنوان، التصنيف، والمالك)');
                return false;
            }
        }
        if (stepId === 'location') {
            if (!stepData.ar.address) {
                setError('يرجى إدخال عنوان العقار (باللغة العربية على الأقل)');
                return false;
            }
        }
        if (stepId === 'features') {
            if (!stepData.rooms || !stepData.bathrooms || !stepData.area) {
                setError('يرجى إدخال عدد الغرف، عدد الحمامات، والمساحة');
                return false;
            }
        }
        return true;
    };

    const collectData = () => {
        let currentStepData: any = {};
        const stepId = steps[currentStep].id;

        if (stepId === 'basic') currentStepData = basicRef.current?.getData();
        else if (stepId === 'location') currentStepData = locationRef.current?.getData();
        else if (stepId === 'pricing') currentStepData = pricingRef.current?.getData();
        else if (stepId === 'features') currentStepData = featuresRef.current?.getData();
        else if (stepId === 'media') {
            const mediaData = mediaRef.current?.getData();
            if (mediaData) {
                setMainImageFile(mediaData.mainImageFile);
                setGalleryFiles(mediaData.galleryFiles);
                setExistingImages(mediaData.existingImages);
                setDeletedMediaIds(mediaData.deletedMediaIds || []);
            }
            return { ...formData }; // Media doesn't go to formData directly
        }
        return { ...formData, ...currentStepData };
    };

    const handleNext = async () => {
        if (submitting) return;

        const currentStepId = steps[currentStep].id;

        // Sync & Validate
        const updatedFormData = collectData();
        if (!validateStep(currentStep, updatedFormData)) return;

        // Commit to global state only on successful navigation
        setFormData(updatedFormData);

        try {
            if (currentStepId !== 'confirm') {
                setCurrentStep(prev => prev + 1);
                window.scrollTo(0, 0);
                return;
            }

            setSubmitting(true);
            const submissionData = {
                ...updatedFormData,
                existing_media_ids: existingImages.map(img => img.id)
            };

            let createdIdResponse = id;
            if (id) {
                await PropertyService.updateProperty(Number(id), submissionData as any);

                // Unified Background Media Update (Uploads + Deletions)
                const newFiles: File[] = [];
                if (mainImageFile) newFiles.push(mainImageFile);
                if (galleryFiles.length > 0) newFiles.push(...galleryFiles);

                if (newFiles.length > 0 || deletedMediaIds.length > 0) {
                    uploadFiles(Number(id), newFiles, 'property_image', deletedMediaIds);
                }
            } else {
                const response: any = await PropertyService.createProperty(submissionData as any);
                // The backend returns { id_property: 1, status: "success", ... }
                createdIdResponse = response.id_property?.toString();

                if (!createdIdResponse) {
                    console.warn('Backend did not return id_property, using fallback or empty');
                }

                // Trigger background uploads for new property using state
                if (createdIdResponse) {
                    const newFiles: File[] = [];
                    if (mainImageFile) newFiles.push(mainImageFile);
                    if (galleryFiles.length > 0) newFiles.push(...galleryFiles);

                    if (newFiles.length > 0) {
                        uploadFiles(Number(createdIdResponse), newFiles, 'property_image');
                    }
                }
            }

            navigate('/properties');
        } catch (err: any) {
            console.error('Submission failed:', err);
            setError(err.response?.data?.message || 'حدث خطأ أثناء حفظ البيانات.');
        } finally {
            setSubmitting(false);
        }
    };

    const handleQuickSave = async () => {
        if (submitting) return;
        setSubmitting(true);
        try {
            const updatedFormData = collectData();
            setFormData(updatedFormData);

            const submissionData = {
                ...updatedFormData,
                existing_media_ids: existingImages.map(img => img.id)
            };

            await PropertyService.updateProperty(Number(id), submissionData as any);

            // Handle deletions if any
            const mediaData = mediaRef.current?.getData();
            if (mediaData?.deletedMediaIds?.length) {
                for (const mId of mediaData.deletedMediaIds) {
                    try {
                        await PropertyService.deleteMedia(mId);
                    } catch (e) {
                        console.error(`Failed to delete media ${mId}:`, e);
                    }
                }
            }

            navigate('/properties');
        } catch (err: any) {
            console.error('Quick Save failed:', err);
            setError(err.response?.data?.message || 'حدث خطأ أثناء حفظ التعديلات السريعة.');
        } finally {
            setSubmitting(false);
        }
    };

    const handleBack = () => {
        setCurrentStep(prev => prev - 1);
    };

    return (
        <div className="max-w-6xl mx-auto py-8 px-4 sm:px-6 lg:px-8 bg-white rounded-3xl shadow-xl shadow-gray-200/50 border border-gray-100 min-h-[600px] relative">
            {fetchingData && (
                <div className="absolute inset-0 bg-white/80 backdrop-blur-[2px] z-50 rounded-3xl flex flex-col items-center justify-center animate-fade-in">
                    <div className="p-8 bg-white rounded-2xl shadow-2xl border border-gray-100 flex flex-col items-center gap-4">
                        <div className="relative">
                            <div className="w-16 h-16 border-4 border-primary/20 border-t-primary rounded-full animate-spin" />
                            <Loader2 className="w-8 h-8 text-primary animate-pulse absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2" />
                        </div>
                        <div className="text-center">
                            <h3 className="text-lg font-bold text-gray-800">جاري تحميل بيانات العقار...</h3>
                            <p className="text-sm text-gray-500 mt-1">يرجى الانتظار قليلاً</p>
                        </div>
                    </div>
                </div>
            )}

            <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 mb-10 pb-6 border-b border-gray-100">
                <button onClick={() => navigate('/properties')} className="p-2 hover:bg-gray-100 rounded-lg">
                    {i18n.dir() === 'rtl' ? <ChevronRight /> : <ChevronLeft />}
                </button>
                <div>
                    <h2 className="text-2xl font-bold text-primary">{id ? t('property_form.edit_title') : t('property_form.add_title')}</h2>
                    <p className="text-v3-text-secondary text-sm">{t('property_form.subtitle')}</p>
                </div>
            </div>

            {error && (
                <div className="bg-red-50 border border-red-100 text-red-600 px-4 py-3 rounded-xl text-sm animate-shake">
                    <span>{error}</span>
                </div>
            )}

            <div className="flex items-center justify-between relative mb-10 px-4">
                <div className="absolute top-1/2 left-0 w-full h-1 bg-gray-200 -z-10 rounded-full"></div>
                <div className="absolute top-1/2 left-0 h-1 bg-primary -z-10 rounded-full transition-all duration-300" style={{ width: `${(currentStep / (steps.length - 1)) * 100}%` }}></div>
                {steps.map((step, index) => {
                    const isAccessible = index <= currentStep || id || createdId;
                    return (
                        <div
                            key={step.id}
                            onClick={() => {
                                if (isAccessible && index < currentStep) {
                                    setCurrentStep(index);
                                }
                            }}
                            className={`flex flex-col items-center gap-2 bg-white px-2 ${isAccessible ? 'cursor-pointer' : 'cursor-not-allowed grayscale'}`}
                        >
                            <div className={`w-10 h-10 rounded-full flex items-center justify-center transition-all duration-300 ${index === currentStep ? 'bg-primary text-white scale-110 shadow-lg ring-4 ring-primary/20' : index < currentStep ? 'bg-primary text-white' : 'bg-gray-100 text-gray-400'}`}>
                                <step.icon className="w-5 h-5" />
                            </div>
                            <span className={`text-xs font-medium ${index === currentStep ? 'text-primary font-bold' : 'text-gray-500'}`}>{step.label}</span>
                        </div>
                    );
                })}
            </div>

            <div className="bg-white rounded-2xl shadow-xl border border-v3-border p-6 md:p-8 min-h-[400px] relative">
                {submitting && (
                    <div className="absolute inset-0 bg-white/50 backdrop-blur-[1px] z-10 flex items-center justify-center rounded-2xl">
                        <div className="bg-white p-4 rounded-xl shadow-lg border border-gray-100 flex items-center gap-3">
                            <Loader2 className="w-6 h-6 animate-spin text-primary" />
                            <span className="text-sm font-medium text-gray-700">{t('common.processing')}</span>
                        </div>
                    </div>
                )}
                {steps[currentStep].id === 'basic' && <BasicInfoStep ref={basicRef} t={t} formData={formData} categories={categories} owners={owners} />}
                {steps[currentStep].id === 'location' && <LocationStep ref={locationRef} t={t} formData={formData} />}
                {steps[currentStep].id === 'pricing' && <PricingStep ref={pricingRef} t={t} formData={formData} />}
                {steps[currentStep].id === 'features' && <FeaturesStep ref={featuresRef} t={t} formData={formData} />}
                {steps[currentStep].id === 'media' && (
                    <MediaStep
                        ref={mediaRef}
                        t={t}
                        mainImageFile={mainImageFile}
                        galleryFiles={galleryFiles}
                        existingImages={existingImages}
                    />
                )}
                {steps[currentStep].id === 'confirm' && (
                    <ConfirmationStep
                        t={t}
                        formData={formData}
                        mainImageFile={mainImageFile}
                        galleryFiles={galleryFiles}
                        existingImages={existingImages}
                    />
                )}
            </div>

            <div className="flex items-center justify-between pt-4 gap-4">
                <div className="flex items-center gap-3">
                    <Button type="button" variant="outline" onClick={handleBack} disabled={currentStep === 0} className={`px-6 ${currentStep === 0 ? 'opacity-0' : ''}`}>
                        {t('common.back')}
                    </Button>

                    {id && (
                        <Button
                            type="button"
                            variant="outline"
                            onClick={handleQuickSave}
                            disabled={submitting}
                            className="px-6 border-blue-200 text-blue-600 hover:bg-blue-50 flex items-center gap-2"
                        >
                            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
                            <span>حفظ التعديلات</span>
                        </Button>
                    )}
                </div>

                <Button type="button" onClick={handleNext} disabled={submitting} className="px-8 bg-primary text-white hover:bg-primary/90 shadow-lg shadow-primary/20 flex items-center gap-3">
                    {submitting && <Loader2 className="w-5 h-5 animate-spin" />}
                    {currentStep === steps.length - 1 ? t('common.save') : t('common.next')}
                    {currentStep < steps.length - 1 && (i18n.dir() === 'rtl' ? <ChevronLeft className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />)}
                </Button>
            </div>
        </div>
    );
};

export default PropertyForm;
